import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { STATUS_COLORS } from "@/lib/meta";

const StatusContext = createContext(null);

export function StatusProvider({ children }) {
  const { user } = useAuth();
  const [statuses, setStatuses] = useState([]);

  const refresh = useCallback(() => {
    if (!user) return;
    api.get("/job-statuses").then((r) => setStatuses(r.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const map = useMemo(
    () => Object.fromEntries(statuses.map((s) => [s.key, s])),
    [statuses]
  );

  const metaFor = useCallback(
    (key) => {
      const s = map[key];
      return {
        label: s?.label || key,
        cls: STATUS_COLORS[s?.color] || STATUS_COLORS.zinc,
      };
    },
    [map]
  );

  // ordered keys; optional filter to exclude completed statuses
  const orderedKeys = useCallback(
    (opts = {}) =>
      statuses
        .filter((s) => (opts.excludeCompleted ? !s.is_completed : true))
        .map((s) => s.key),
    [statuses]
  );

  const value = useMemo(
    () => ({ statuses, map, metaFor, orderedKeys, refresh }),
    [statuses, map, metaFor, orderedKeys, refresh]
  );

  return <StatusContext.Provider value={value}>{children}</StatusContext.Provider>;
}

export const useStatuses = () => useContext(StatusContext);
